<template>
    <div v-if="isOpen" class="cookie-popup-wrap" :class="{ 'is-open': isOpen }">
      <div class="cookie-closed-view">
        <div>
            <!-- Page 1: Cookie Modal -->
            <div v-if="page === 'cookieModal'" class="cookie-manager-container">
                <p class="cookie-text">
                We use some cookies to continually deliver you the best experience on our website.
                Rest assured that we will always treat your personal details with care.
                <NuxtLink to="/privacypolicy">Read more.</NuxtLink>
                </p>  
                <div class="button-grid">
                    <input type="button" @click="giveConsent" class="fa-btn fa-btn-primary fa-btn-modal" value="Let's go!">
                    <input type="button" @click.stop="switchToManageSettings" class="fa-btn fa-btn-secondary fa-btn-modal" value="Manage settings">                   
                </div>
            </div>


            <!-- Page 2: Cookie Management -->
            <div v-else class="cookie-settings">        
              <div class="checkbox-container">
                  <input id="necessary" v-model="cookiePreferences.necessary" type="checkbox" disabled>
                  <label for="necessary">Necessary Cookies</label>
                  <p class="cookie-description">Purpose: Essential site operations like session management and security. Cannot be disabled.</p>
              </div>
              <div class="checkbox-container">
                  <input id="preferences" v-model="cookiePreferences.preferences" type="checkbox">
                  <label for="preferences">Preference Cookies</label>
                  <p class="cookie-description">Purpose: Store user interface customization like theme or language settings. Enhances usability but not critical.</p>
              </div>
              <div class="checkbox-container">
                  <input id="statistics" v-model="cookiePreferences.statistics" type="checkbox">
                  <label for="statistics">Statistics Cookies</label>
                  <p class="cookie-description">Purpose: Collect anonymized user data for performance analysis, like page visit frequency, browsing patterns.</p>
              </div>
              <div class="checkbox-container">
                  <input id="marketing" v-model="cookiePreferences.marketing" type="checkbox">
                  <label for="marketing">Marketing Cookies</label>
                  <p class="cookie-description">Purpose: Track and analyze online behavior for targeted advertising and campaign effectiveness. May involve third-party data sharing.</p>
              </div>

            <div class="button-group">
                <input type="button" @click="savePreferences" class="fa-btn fa-btn-primary fa-btn-modal" value="Save Preferences"><br><br>
                <span><a href="" @click="switchToCookieBar">Go back</a></span>
            </div>
            </div>

            </div>
      </div>
  
      <!-- Expanded state content -->
      <div v-if="isOpen && 0" class="cookie-popup">
        <i class="fa-regular fa-comment"></i>
        <!-- Your existing content -->
        <div class="speech-bubble">
           
        </div>
        <div class="close-button" @click="togglePopup">X</div>
      </div>
    </div>
  </template>

<script setup>
import { ref, onMounted } from 'vue';
import CookieManager from '~/utils/cookieManager.ts';
import { useUIStore } from '~/store/uiStore';
import { useMatomo } from '~/composables/useMatomo'
import useGTag from '@/composables/useGTag';
import { useInspectlet } from '@/composables/useInspectlet';

const isOpen = ref(false);

function togglePopup() {
  isOpen.value = !isOpen.value;
}

const cookieManager = CookieManager.getInstance();
const consentGiven = computed(() => cookieManager.hasGivenConsent());

const uiStore = useUIStore();

const page = ref('cookieModal'); // Default to showing the cookie bar

// Fetch cookie preferences from the CookieManager utility
const cookiePreferences = ref({
  necessary: true, // Always true
  preferences: false,
  statistics: false,
  marketing: false,
});

function getPreferencesFromManager()
{
  const storedPreferences = cookieManager.getUserPreferences();
  for (let key in storedPreferences) {
    if (cookiePreferences.value[key] !== undefined) {
      cookiePreferences.value[key] = storedPreferences[key];
    }
  }  
}


onMounted(() => {
  console.log("adf consent before")
    if(!consentGiven.value) {
    console.log("Cookie consent not given, showing modal.");
    isOpen.value = true;
  }
  else
  {
    console.log("Cookie consent already given, not showing modal.");
    isOpen.value = false;
  }
  onPreferenceChange();
});


function onPreferenceChange() {
  console.log("adf updating or loading cookie preferences");
  getPreferencesFromManager();

  const { initGTag } = useGTag();
  const { initInspectlet } = useInspectlet();
  const { initMatomo } = useMatomo();

  // Directly using the cookiePreferences values as arguments
  // GTM is loaded if marketing consent is given
  // GA is loaded if statistics consent is given
  initGTag(cookiePreferences.value.marketing, cookiePreferences.value.statistics);

  if (cookiePreferences.value.statistics) {
    console.log("User consented to statistics cookies. Initializing Matomo...");
    initMatomo();
    initInspectlet();
  }
}

function switchToManageSettings() {
    console.log("Switching to manage settings");
  page.value = 'manageSettings';
}

function switchToCookieBar() {
    console.log("Switching to cookie bar");
  page.value = 'cookieModal';
}

function giveConsent() {
  const allTruePreferences = {
    necessary: true,
    preferences: true,
    statistics: true,
    marketing: true,
  };
  cookieManager.setUserPreferences(allTruePreferences);
  onPreferenceChange();
  togglePopup();
}

function savePreferences() {
  cookieManager.setUserPreferences(cookiePreferences.value);
  onPreferenceChange();
  togglePopup();
}

</script>

<style scoped lang="scss">
@import '~/assets/constants.scss';

.cookie-popup-wrap {
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center; // Ensure content and buttons are centered
  color: $dark-mode-color;
  left: 5rem;
  right: 5rem;
  bottom: 2rem;
  z-index: 1000; /* high z-index to ensure it's above other content */
  background: linear-gradient(to bottom left, $header-gray, $light-black);  
  padding: 2.5rem;
  border-radius: $fa-border-radius;

  @include respond-to("tablet-only") {
      left: 2.5rem;
      right: 2.5rem;
    }


  @include respond-to("phone-only") {
      left: 1rem;
      right: 1rem;
    }

  &:before { // Triangle at the bottom
    content: '';
    position: absolute;
    bottom: -2rem; // Adjust this value to change the triangle's position
    left: 50%;
    transform: translateX(-50%); // Center the triangle
    border-left: 2rem solid transparent;
    border-right: 2rem solid transparent;
    border-top: 2rem solid darken($header-gray, 7.5%); // Use the darker color of your gradient for the triangle
  }
  .cookie-text {
    padding: 1rem; // Add padding around the text
    // ... any other styles for this text
  }

  .button-grid {
    display: flex;
    justify-content: center; // This will keep buttons centered
    gap: 1rem; // This adds space between buttons
    padding: 1rem; // Add padding around the button grid
    // ... any other styles for the buttons
  }

  input[type="button"] {
    padding: 0.5rem 1rem; // Add padding inside each button
    // ... any other button styles
  }
}

.cookie-closed-view {
  /* styles for your closed state button/icon */
}

.cookie-description {
  font-style: italic;
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  /* additional styles for your close button */
}

/* Additional styles... */
</style>